/**
 * Cart toast components that read the notification queue of `@ryerson/frontend.application`
 * (`useNotifications`).
 *
 * Live export: {@link MobileTopNotificationsContainer}, which pins every queued `"add-to-cart"`
 * message to the top of the screen. Rendered by product details (mobile), catalog product details,
 * the search list view, quick entry (mobile), order details, all orders, invoices, the
 * shopping-list dashboard and the shopping-list details header.
 *
 * {@link AddToCartNotification}, {@link RemoveFromCartNotification} and
 * {@link NotificationsContainer} have no importer. The pages that send cart toasts import the
 * `AddToCartNotification`, `RemoveFromCartNotification` and `RemoveNotificationContainer` of
 * `@ryerson/frontend.application` instead (for example `components/Catalog/CatalogItems.tsx` →
 * `sendAddToCartNotification`, `components/ShoppingCart/ShoppingCart.tsx` →
 * `sendRemoveFromCartNotification`). The local copies show a fixed "3 Days" lead time and format
 * a price with `displayPrice` directly, so a $0 price would read "$0.00". The package's add-to-cart
 * toast shows "In cart" for a 0 or missing extended price. For a $0 or missing price, "In cart" is
 * accepted there in place of "TBD"; the longer "Price shown in cart" does not fit the limited space
 * to display. The rule a price must keep: "the APIs may return a $0 value for price in which case
 * we always show "TBD" to the end user and never "$0"".
 *
 * Message `type` strings must match what senders put on the queue: `"add-to-cart"` (the package's
 * `NotificationsType.AddToCart`) and `"remove-from-cart"`.
 */
import React, { useState } from "react";
import styled from "@emotion/styled";
import { Typography } from "@ryerson/frontend.typography";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Message, useApplication, useNotifications } from "@ryerson/frontend.application";
import { useTheme } from "@ryerson/frontend.theme";
import { css } from "@emotion/react";
import { Button } from "@ryerson/frontend.button";
import { Media } from "@ryerson/frontend.next";
import { NotificationContent } from "./NotificationContent";
import { addImageDefaultSrc, getProductFormShapeImagePath } from "@components/Helper/common";
import { Link } from "@ryerson/frontend.navigation";
import { Icon } from "@ryerson/frontend.assets";
import { getDisplayUom, navigateToProductDetails } from "@ryerson/frontend.common";
import { displayPrice, IndexedLayers } from "@ryerson/frontend.common";
import { ShoppingCartPricingResponse } from "@ryerson/frontend.common";
import { getPricePerUomAndExtendedPriceOfItem } from "@components/Helper/pricingResponseHelpers";
import { withBasePath } from "@ryerson/frontend.next";

/** Props of {@link AddToCartNotification}: one product just added to the cart. */
export interface CartNotificationProps {
	product: any;
	messageId: number;
	quantity: number;
	qtyUOM: string;
	extendedPrice: any;
	willDisplayPriceInCart?: boolean;
}

/** Props of {@link RemoveFromCartNotification}: the lines just removed from the cart. */
export interface MultipleCartNotificationProps {
	products: any;
	messageId: number;
	/** Restores the removed lines; called by Undo before the toast is dismissed. */
	undoChanges: () => void;
	/** Cart pricing response; row `i` of `products` reads the line with `itemNumber` `i + 1`. */
	cartPricingData: ShoppingCartPricingResponse;
}

const NotificationsPane = styled.div`
	position: fixed;
	bottom: 0;
	left: 0;
	z-index: 9;
	width: 400px;
	box-shadow: 0px 100px 80px rgba(0, 0, 0, 0.07), 0px 0px 17.869px rgba(0, 0, 0, 0.0417275),
		0px 6.6501px 5.32008px rgba(0, 0, 0, 0.0282725);
	${(props: any) => {
		const { breakpoints } = props;
		return `
            @media screen and (max-width: ${breakpoints.lg}) {
                width: 100%
            }
        `;
	}};
`;

const MobileTopNotificationsPane = styled.div`
	position: fixed;
	width: 100%;
	top: 0;
	left: 0;
	overflow: hidden;
	z-index: ${IndexedLayers.pageLevel3};
	box-shadow: 0px 100px 80px rgba(0, 0, 0, 0.07), 0px 0px 17.869px rgba(0, 0, 0, 0.0417275),
		0px 6.6501px 5.32008px rgba(0, 0, 0, 0.0282725);
`;

const Notification = styled.div`
	width: 100%;
	max-height: 530px;
	overflow-y: auto;
	border-radius: 2px;
	${(props: any) => {
		const { bgColor, borderColor, isNative } = props;
		return css`
			border: 1px solid ${borderColor};
			background: ${bgColor};
			margin-top: ${isNative ? "60px" : "0"};
		`;
	}};
`;

const NotificationExpandable = styled.div`
	width: 100%;
	max-height: 50vh;
	overflow-y: auto;
	${(props: any) => {
		const { bgColor, isNative } = props;
		return css`
			background: ${bgColor};
			margin-bottom: ${isNative ? "60px" : "0"};
		`;
	}};
`;

const Caret = styled.div<any>`
	width: 0;
	height: 0;
	top: -${(props) => props.size * 2}px;
	border: ${(props) => props.size}px solid transparent;
	left: calc(50% - ${(props) => props.size}px / 2);
	align-self: ${(props) => props.alignSelf};
	border-bottom-color: ${(props) => props.bgColor};
	position: absolute;
	margin-bottom: -${(props) => props.size * 2}px;
`;

const CaretButton = styled.div<any>`
	position: absolute;
	top: -10px;
	left: calc(50% + 2px);
`;

const NotificationHeader = styled.div`
	max-width: 100%;
	padding: 20px;
	p {
		margin: 0;
	}
	${(props: any) => {
		const { bgColor } = props;
		return css`
			background: ${bgColor};
		`;
	}};
`;

const NotificationContainer = styled(Flex)`
	padding: 0 20px;
`;

const NotificationContainerMobile = styled(Flex)<any>`
	padding: 10px 20px 0;
	border-top: 1px solid ${(props) => props.borderColor};
`;

const PriceContainer = styled(Flex)`
	width: 100%;
	padding: 0 0 20px;
`;

const NotificationImageContainer = styled(FlexItem)`
	width: 25%;
	margin: 0 20px 0 0;
	${(props: any) => {
		const { imageBorderColor } = props;
		return css`
			img {
				margin: 15px auto 0;
				border-radius: 2px;
				border: 1px solid ${imageBorderColor};
			}
		`;
	}};
`;

const NotificationLink = styled.div`
	width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	${(props: any) => {
		const { color } = props;
		return css`
			color: ${color};
		`;
	}};
`;

/**
 * "Added to Cart" toast for one product: image, product link (plain text when the product is not
 * `active`), quantity and extended price or "In cart". No importer; pages use the package
 * component of the same name (see the file header).
 */
export const AddToCartNotification: React.FC<CartNotificationProps> = ({
	product,
	messageId,
	qtyUOM,
	quantity,
	extendedPrice,
	willDisplayPriceInCart = false,
}) => {
	const { removeNotification } = useNotifications();
	const { theme } = useTheme();
	const {
		isNative,
		localization: { language },
	} = useApplication();
	const content = NotificationContent[language] || NotificationContent.en;
	const { white, lighterGray, darkGray } = theme.colors.primary;
	const { lightBlue, darkBlue } = theme.colors.secondary;
	const displayExtendedPrice = displayPrice(extendedPrice);

	return (
		<>
			<Media lessThan="lg">
				<Notification {...{ bgColor: white, borderColor: lighterGray, isNative: isNative }}>
					<NotificationHeader {...{ bgColor: darkGray }}>
						<Flex justifyContent="space-between">
							<FlexItem>
								<Typography variant="p" size="xl" type="tertiary" color={white}>
									{content.addTitle}
								</Typography>
							</FlexItem>
							<Button
								label=""
								type="secondary"
								variant="outline"
								shape="circle"
								leftIcon="close"
								size="xs"
								background={white}
								onClick={() => {
									removeNotification();
								}}
							/>
						</Flex>
					</NotificationHeader>
					<NotificationContainer>
						<NotificationImageContainer {...{ imageBorderColor: lighterGray }}>
							<img
								onError={(event) => addImageDefaultSrc(event)}
								src={getProductFormShapeImagePath(
									product?.category,
									product?.form,
									product?.shape
								)}
								width="100%"
							/>
						</NotificationImageContainer>
						<PriceContainer direction="column">
							<FlexItem>
								<NotificationLink css={{ marginTop: "15px" }} color={lightBlue}>
									{product?.active ? (
										<Link
											size="md"
											to={navigateToProductDetails(
												product?.id,
												product?.altPartNumber
											)}
										>
											{product.fastPath}
										</Link>
									) : (
										<Typography color={darkGray}>{product.fastPath}</Typography>
									)}
								</NotificationLink>
							</FlexItem>
							<FlexItem>
								<Typography size="md" variant="p" type="primary">
									{product.altPartNumber
										? `${product.altPartNumber}`
										: `${product.materialMaster} `}
								</Typography>
							</FlexItem>
							<Flex justifyContent="space-between">
								<Typography size="xs" variant="span" type="primary">
									{`${content.leadTime} 3 Days`}
								</Typography>
								<Typography size="xs" variant="span" type="primary">
									{content.extendedPrice}
								</Typography>
							</Flex>
							<Flex justifyContent="space-between">
								<FlexItem>
									<Typography size="xs" variant="span" type="primary">
										{content.qty}
									</Typography>
									<Typography size="xs" variant="span" color={darkBlue}>
										{`${quantity} ${getDisplayUom(
											product?.form,
											qtyUOM,
											"quantity"
										)}`}
									</Typography>
								</FlexItem>
								{willDisplayPriceInCart ? (
									<Typography size="xs" variant="span" type="primary">
										{content.priceInCart}
									</Typography>
								) : (
									<>
										<Typography
											size="sm"
											variant="span"
											weight="bolder"
											color={darkBlue}
										>
											{displayExtendedPrice}
										</Typography>
									</>
								)}
							</Flex>
						</PriceContainer>
					</NotificationContainer>
				</Notification>
			</Media>
			<Media greaterThanOrEqual="lg">
				<Notification {...{ bgColor: white, borderColor: lighterGray }}>
					<NotificationHeader {...{ bgColor: lighterGray }}>
						<Flex justifyContent="space-between">
							<FlexItem>
								<Typography variant="p" size="lg" type="primary">
									{content.addTitle}
								</Typography>
							</FlexItem>
							<Button
								label=""
								type="secondary"
								variant="solid"
								shape="circle"
								leftIcon="close"
								size="xs"
								onClick={() => {
									removeNotification();
								}}
							/>
						</Flex>
					</NotificationHeader>
					<NotificationContainer>
						<NotificationImageContainer {...{ imageBorderColor: lighterGray }}>
							<img
								onError={(event) => addImageDefaultSrc(event)}
								src={getProductFormShapeImagePath(
									product?.category,
									product?.form,
									product?.shape
								)}
								width="100%"
							/>
							<NotificationLink css={{ marginBottom: "15px" }} color={lightBlue}>
								{product?.active ? (
									<Link
										size="sm"
										to={navigateToProductDetails(
											product.id,
											product?.altPartNumber
										)}
									>
										{product.fastPath}
									</Link>
								) : (
									<Typography color={darkGray}>{product.fastPath}</Typography>
								)}
							</NotificationLink>
						</NotificationImageContainer>
						<PriceContainer direction="column">
							<Typography size="sm" variant="p" type="primary">
								{`${content.alt} ${product.materialMaster} ${product.description}`}
							</Typography>
							<Flex justifyContent="space-between">
								<Typography size="xs" variant="span" type="primary">
									{`${content.leadTime} 3 Days`}
								</Typography>
								<Typography size="xs" variant="span" type="primary">
									{content.extendedPrice}
								</Typography>
							</Flex>
							<Flex justifyContent="space-between">
								<FlexItem>
									<Typography size="xs" variant="span" type="primary">
										{content.qty}
									</Typography>
									<Typography size="xs" variant="span" color={darkBlue}>
										{`${quantity} ${getDisplayUom(
											product?.form,
											qtyUOM,
											"quantity"
										)}`}
									</Typography>
								</FlexItem>
								{willDisplayPriceInCart ? (
									<Typography size="xs" variant="span" type="primary">
										{content.priceInCart}
									</Typography>
								) : (
									<>
										<Typography
											size="sm"
											variant="span"
											weight="bolder"
											color={darkBlue}
										>
											{displayExtendedPrice}
										</Typography>
									</>
								)}
							</Flex>
						</PriceContainer>
					</NotificationContainer>
				</Notification>
			</Media>
		</>
	);
};

/**
 * "Removed from Cart" toast listing the removed lines, with Undo (calls `undoChanges`, then
 * dismisses). On mobile the list is collapsed behind a caret. No importer; the cart uses the
 * package component of the same name (see the file header).
 */
export const RemoveFromCartNotification: React.FC<MultipleCartNotificationProps> = ({
	products,
	undoChanges,
	cartPricingData,
}) => {
	const { removeNotification } = useNotifications();
	const { theme } = useTheme();
	const [isNotificationsExpanded, setIsNotificationsExpanded] = useState<boolean>(false);
	const {
		isNative,
		localization: { language },
	} = useApplication();
	const content = NotificationContent[language] || NotificationContent.en;
	const { white, lighterGray, gray, darkGray } = theme.colors.primary;
	const { lightBlue, darkBlue } = theme.colors.secondary;

	const displayExtendedPrice = (
		rowIndex: number,
		cartPricingData: ShoppingCartPricingResponse
	) => {
		let priceData = getPricePerUomAndExtendedPriceOfItem(rowIndex, cartPricingData);
		let currentExtendedPrice = Number(priceData.extendedPrice);
		let extendedPrice = Number.isNaN(currentExtendedPrice)
			? priceData.extendedPrice
			: displayPrice(currentExtendedPrice);
		return extendedPrice;
	};
	const caretSize = 15;

	return (
		<>
			<Media lessThan="lg">
				<NotificationExpandable {...{ bgColor: white, isNative: isNative }}>
					<Caret
						className="caret"
						{...{
							size: caretSize,
							alignSelf: "center",
							left: "auto",
							right: "auto",
							bgColor: white,
						}}
					/>
					<CaretButton {...{ size: caretSize }}>
						<Icon
							size="xs"
							icon={isNotificationsExpanded ? "chevron-down" : "chevron-up"}
							onClick={() => {
								setIsNotificationsExpanded(!isNotificationsExpanded);
							}}
						/>
					</CaretButton>
					<NotificationHeader {...{ bgColor: white }}>
						<Flex justifyContent="space-between">
							<FlexItem>
								<Typography variant="p" size="lg" type="secondary">
									{content.removeTitle} &nbsp;
									<Typography
										variant="span"
										size="sm"
										color={gray}
										type="primary"
									>
										{products.length}{" "}
										{`${content.product}${products.length > 1 ? "s" : ""}`}
									</Typography>
								</Typography>
								<Flex
									alignItems="center"
									css={{ cursor: "pointer" }}
									onClick={() => {
										undoChanges();
										removeNotification();
									}}
								>
									<Icon
										icon="revert"
										size="sm"
										color={lightBlue}
										css={{ margin: "5px", cursor: "pointer" }}
									/>
									<Typography
										color={lightBlue}
										type="secondary"
										size="md"
										css={{ marginRight: "5px" }}
									>
										{content.undo}
									</Typography>
								</Flex>
							</FlexItem>
							<FlexItem>
								<Button
									label=""
									type="secondary"
									variant="outline"
									shape="circle"
									leftIcon="close"
									size="xs"
									background={lightBlue}
									onClick={() => {
										removeNotification();
									}}
								/>
							</FlexItem>
						</Flex>
					</NotificationHeader>
					{isNotificationsExpanded &&
						products.map((product: any, index: number) => {
							return (
								<React.Fragment key={product.id}>
									<NotificationContainerMobile {...{ borderColor: lighterGray }}>
										<NotificationImageContainer
											{...{ imageBorderColor: lighterGray }}
										>
											<img
												onError={(event) => addImageDefaultSrc(event)}
												src={withBasePath(`/ProductDetailsImage.png`)}
												width="100%"
											/>
										</NotificationImageContainer>
										<PriceContainer direction="column">
											<FlexItem>
												<NotificationLink
													css={{ marginTop: "15px" }}
													color={lightBlue}
												>
													{product?.active ? (
														<Link
															size="md"
															to={navigateToProductDetails(
																product?.itemId,
																product?.altPartNumber
															)}
														>
															{product.fastPath}
														</Link>
													) : (
														<Typography color={darkGray}>
															{product.fastPath}
														</Typography>
													)}
												</NotificationLink>
											</FlexItem>
											<FlexItem>
												<Typography size="md" variant="p" type="primary">
													ALT: {product.materialMaster}{" "}
													{product.shortDescription}
												</Typography>
											</FlexItem>
											<Flex justifyContent="space-between">
												<Typography size="xs" variant="span" type="primary">
													{`${content.leadTime} 3 Days`}
												</Typography>
												<Typography size="xs" variant="span" type="primary">
													{content.extendedPrice}
												</Typography>
											</Flex>
											<Flex justifyContent="space-between">
												<FlexItem>
													<Typography
														size="xs"
														variant="span"
														type="primary"
													>
														{content.qty}
													</Typography>
													<Typography
														size="xs"
														variant="span"
														color={darkBlue}
													>
														{`${
															product.quantity
														} ${product.quantityUOM.toLowerCase()}`}
													</Typography>
												</FlexItem>
												<Typography
													size="sm"
													variant="span"
													weight="bolder"
													color={darkBlue}
												>
													{displayExtendedPrice(index, cartPricingData)}
												</Typography>
											</Flex>
										</PriceContainer>
									</NotificationContainerMobile>
								</React.Fragment>
							);
						})}
				</NotificationExpandable>
			</Media>
			<Media greaterThanOrEqual="lg">
				<Notification {...{ bgColor: white, borderColor: lighterGray }}>
					<NotificationHeader {...{ bgColor: lighterGray }}>
						<Flex justifyContent="space-between">
							<FlexItem>
								<Typography variant="p" size="lg" type="primary">
									{content.removeTitle}
								</Typography>
								<Typography variant="span" type="secondary">
									({products.length}{" "}
									{`${content.product}${products.length > 1 ? "s" : ""}`})
								</Typography>
							</FlexItem>
							<FlexItem>
								<Flex>
									<FlexItem>
										<Flex
											alignItems="center"
											css={{ cursor: "pointer" }}
											onClick={() => {
												undoChanges();
												removeNotification();
											}}
										>
											<Icon
												icon="revert"
												size="sm"
												color={lightBlue}
												css={{ margin: "5px", cursor: "pointer" }}
											/>
											<Typography
												color={lightBlue}
												type="secondary"
												size="sm"
												css={{ marginRight: "5px" }}
											>
												{content.undo}
											</Typography>
										</Flex>
									</FlexItem>
									<Button
										label=""
										type="secondary"
										variant="outline"
										shape="circle"
										leftIcon="close"
										size="xs"
										background={lightBlue}
										onClick={() => {
											removeNotification();
										}}
									/>
								</Flex>
							</FlexItem>
						</Flex>
					</NotificationHeader>
					{products.map((product: any, index: number) => {
						return (
							<React.Fragment key={index}>
								<NotificationContainer>
									<NotificationImageContainer
										{...{ imageBorderColor: lighterGray }}
									>
										<img
											onError={(event) => addImageDefaultSrc(event)}
											src={withBasePath(`/ProductDetailsImage.png`)}
											width="100%"
										/>
										<NotificationLink
											css={{ marginBottom: "15px" }}
											color={lightBlue}
										>
											{product?.active ? (
												<Link
													size="sm"
													to={navigateToProductDetails(
														product?.itemId,
														product?.altPartNumber
													)}
												>
													{product.fastPath}
												</Link>
											) : (
												<Typography color={darkGray}>
													{product.fastPath}
												</Typography>
											)}
										</NotificationLink>
									</NotificationImageContainer>
									<PriceContainer direction="column">
										<Typography size="sm" variant="p" type="primary">
											ALT: {product.materialMaster} {product.shortDescription}
										</Typography>
										<Flex justifyContent="space-between">
											<Typography size="xs" variant="span" type="primary">
												{`${content.leadTime} 3 Days`}
											</Typography>
											<Typography size="xs" variant="span" type="primary">
												{content.extendedPrice}
											</Typography>
										</Flex>
										<Flex justifyContent="space-between">
											<FlexItem>
												<Typography size="xs" variant="span" type="primary">
													{content.qty}
												</Typography>
												<Typography
													size="xs"
													variant="span"
													color={darkBlue}
												>
													{`${
														product.quantity
													} ${product.quantityUOM.toLowerCase()}`}
												</Typography>
											</FlexItem>
											<Typography
												size="sm"
												variant="span"
												weight="bolder"
												color={darkBlue}
											>
												{displayExtendedPrice(index, cartPricingData)}
											</Typography>
										</Flex>
									</PriceContainer>
								</NotificationContainer>
							</React.Fragment>
						);
					})}
				</Notification>
			</Media>
		</>
	);
};

/**
 * Bottom-left pane showing the most recent queued `"remove-from-cart"` message. No importer; the
 * cart renders `@ryerson/frontend.application` → `RemoveNotificationContainer`.
 */
export const NotificationsContainer: React.FC = () => {
	const { notifications } = useNotifications();
	const { theme } = useTheme();
	return (
		<>
			{notifications.filter((message: Message) => {
				return message.type === "remove-from-cart";
			}).length > 0 && (
				<NotificationsPane {...{ breakpoints: theme.breakpoints }}>
					{
						notifications
							.filter((message: Message) => {
								return message.type === "remove-from-cart";
							})
							.pop()?.message
					}
				</NotificationsPane>
			)}
		</>
	);
};

/**
 * Fixed full-width pane at the top of the viewport that renders the element of every queued
 * `"add-to-cart"` message, in queue order. Renders nothing when there is none. Dismissal and
 * time-outs are handled by the queue in `@ryerson/frontend.application`.
 */
export const MobileTopNotificationsContainer: React.FC = () => {
	const { notifications } = useNotifications();
	return (
		<>
			{notifications?.filter((notification: Message) => {
				return notification.type === "add-to-cart";
			}).length > 0 && (
				<MobileTopNotificationsPane>
					{notifications
						?.filter((notification: Message) => {
							return notification.type === "add-to-cart";
						})
						.map((notification: Message) => {
							return notification.message;
						})}
				</MobileTopNotificationsPane>
			)}
		</>
	);
};
